/** Aktions-Kalender: reine Visualisierung der Sales-Aktionen im Monat (Gantt-Balken).
 *  Konflikte werden nur angezeigt — die Pricing-Auflösung bleibt ausschließlich im Backend. */
import { useMemo, useState } from "react";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common";
import { ChevronLeft, ChevronRight, CalendarDays, AlertTriangle } from "lucide-react";

const day = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`) : null);
const iso = (d) => d.toISOString().slice(0, 10);
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

const inter = (a = [], b = []) => a.some((x) => b.includes(x));

/** Konflikt = Zeitraum UND Artikel UND Pricing-Kanal überschneiden sich. */
const itemsClash = (a, b) => {
  const aAll = !!a.applies_to_all, bAll = !!b.applies_to_all;
  const aHas = (a.product_ids || []).length + (a.variant_ids || []).length + (a.bundle_ids || []).length > 0;
  const bHas = (b.product_ids || []).length + (b.variant_ids || []).length + (b.bundle_ids || []).length > 0;
  if (aAll && bAll) return true;
  if (aAll) return bAll || bHas;
  if (bAll) return aHas;
  return inter(a.product_ids, b.product_ids) || inter(a.variant_ids, b.variant_ids)
    || inter(a.bundle_ids, b.bundle_ids);
};
const channelClash = (a, b) => (!!a.applies_to_b2c && !!b.applies_to_b2c)
  || inter(a.applicable_price_list_ids, b.applicable_price_list_ids);
const timeClash = (a, b) => day(a.valid_from) <= day(b.valid_until) && day(b.valid_from) <= day(a.valid_until);
const pricingRelevant = (p) => !p.archived && ["aktiv", "geplant"].includes(p.status);

const BAR = {
  aktiv: "bg-primary/25 border-primary/50 text-primary",
  geplant: "bg-chart-4/25 border-chart-4/50 text-chart-4",
  abgelaufen: "bg-muted border-border text-muted-foreground",
  deaktiviert: "bg-muted border-dashed border-border text-muted-foreground",
};

export default function SalesPromotionCalendar({ rows, onEdit }) {
  const { t, fmtDate } = useI18n();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const total = daysInMonth(y, m);
  const first = new Date(y, m, 1), last = new Date(y, m, total);
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const todayCol = today.getFullYear() === y && today.getMonth() === m ? today.getDate() : null;

  const conflicts = useMemo(() => {
    const map = {};
    const rel = (rows || []).filter(pricingRelevant);
    for (let i = 0; i < rel.length; i++) {
      for (let j = i + 1; j < rel.length; j++) {
        const a = rel[i], b = rel[j];
        if (timeClash(a, b) && itemsClash(a, b) && channelClash(a, b)) {
          (map[a.id] = map[a.id] || []).push(b.name);
          (map[b.id] = map[b.id] || []).push(a.name);
        }
      }
    }
    return map;
  }, [rows]);

  const bars = useMemo(() => (rows || [])
    .filter((p) => p.valid_from && p.valid_until && day(p.valid_from) <= last && day(p.valid_until) >= first)
    .map((p) => {
      const from = day(p.valid_from), until = day(p.valid_until);
      const start = from < first ? 1 : from.getDate();
      const end = until > last ? total : until.getDate();
      return { p, start, end, cutLeft: from < first, cutRight: until > last, conflict: conflicts[p.id] || null };
    })
    .sort((a, b) => a.start - b.start || (a.p.name || "").localeCompare(b.p.name || "", "de")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, y, m, conflicts]);

  const conflictCount = bars.filter((b) => b.conflict).length;
  const statusLabel = (p) => (p.archived ? t("sp.status.archiviert") : t(`sp.status.${p.status}`, p.status));

  return (
    <div data-testid="sp-calendar">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => setCursor(new Date(y, m - 1, 1))} data-testid="spc-prev" aria-label={t("spc.prev")}><ChevronLeft className="h-4 w-4" /></Button>
        <div className="min-w-[11rem] text-center font-heading font-medium capitalize" data-testid="spc-month">{monthLabel}</div>
        <Button size="sm" variant="outline" onClick={() => setCursor(new Date(y, m + 1, 1))} data-testid="spc-next" aria-label={t("spc.next")}><ChevronRight className="h-4 w-4" /></Button>
        <Button size="sm" variant="ghost" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} data-testid="spc-today">{t("spc.today")}</Button>
        {conflictCount > 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive" data-testid="spc-conflict-summary">
            <AlertTriangle className="h-3.5 w-3.5" /> {t("spc.conflictCount", { count: conflictCount })}
          </span>
        )}
      </div>

      {bars.length === 0 ? (
        <EmptyState testId="spc-empty" icon={CalendarDays} title={t("spc.emptyTitle")} hint={t("spc.emptyHint")} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
          <div className="min-w-[46rem] p-3">
            <div className="grid text-[10px] text-muted-foreground" style={{ gridTemplateColumns: `repeat(${total}, minmax(0,1fr))` }}>
              {Array.from({ length: total }, (_, i) => {
                const d = new Date(y, m, i + 1);
                const we = [0, 6].includes(d.getDay());
                return (
                  <div key={i} className={`border-l border-border/60 px-0.5 py-1 text-center ${we ? "bg-muted/50" : ""} ${todayCol === i + 1 ? "font-semibold text-primary" : ""}`}>
                    {i + 1}
                  </div>
                );
              })}
            </div>
            <div className="space-y-1.5 pt-1.5">
              {bars.map(({ p, start, end, cutLeft, cutRight, conflict }) => (
                <div key={p.id} className="grid items-center" style={{ gridTemplateColumns: `repeat(${total}, minmax(0,1fr))` }}>
                  <button type="button" onClick={() => onEdit(p)} title={`${p.name} · ${fmtDate(p.valid_from)} – ${fmtDate(p.valid_until)}${conflict ? ` · ${t("spc.conflictWith", { names: conflict.join(", ") })}` : ""}`}
                    style={{ gridColumn: `${start} / ${end + 1}` }}
                    data-testid={`spc-bar-${p.internal_code}`}
                    className={`flex min-w-0 items-center gap-1.5 overflow-hidden rounded border px-2 py-1.5 text-left text-xs transition-transform hover:-translate-y-px
                      ${cutLeft ? "rounded-l-none" : ""} ${cutRight ? "rounded-r-none" : ""}
                      ${conflict ? "border-destructive bg-destructive/10 text-destructive ring-1 ring-destructive/40" : BAR[p.status] || BAR.abgelaufen}
                      ${p.archived && !conflict ? "opacity-60" : ""}`}>
                    {conflict && <AlertTriangle className="h-3.5 w-3.5 shrink-0" data-testid={`spc-conflict-${p.internal_code}`} />}
                    <span className="truncate font-medium">{p.name}</span>
                    <span className="shrink-0 font-mono opacity-80">{p.discount_type === "percent" ? `−${p.discount_value}%` : `−${p.discount_value}`}</span>
                    <span className="shrink-0 opacity-70">· {statusLabel(p)}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid="spc-legend">
        {["aktiv", "geplant", "abgelaufen", "deaktiviert"].map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5">
            <span className={`inline-block h-2.5 w-4 rounded border ${BAR[s]}`} />{t(`sp.status.${s}`)}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 text-destructive">
          <span className="inline-block h-2.5 w-4 rounded border border-destructive bg-destructive/20" />{t("spc.conflict")}
        </span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground" data-testid="spc-hint">{t("spc.hint")}</p>
    </div>
  );
}
